/* istanbul ignore file */
import { fetchWorkflowTE } from 'api/workflows/fetchWorkflow';
import { fetchAppModelsPageTE } from 'api/models/fetchAppModelsPage';
import { listWorkflowsTE, listWorkflowVersionsTE, listWorkflowsByAppTE, deleteWorkflowTE } from 'api/workflows';
import { taskEither } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { TaskEither } from 'fp-ts/lib/TaskEither';
import { ReactLeft, UI_STATES } from 'utils/uiStates/uiStates';
import { errorToReactLeft } from 'utils/fp';

const workflowVersionTypeGuard = (
  workflow: CF.API.Workflows.WorkflowRes | CF.API.Workflows.WorkflowVersionDetailsRes,
): workflow is CF.API.Workflows.WorkflowVersionDetailsRes => {
  return 'workflow_version' in workflow;
};

export const workflowPageApiEffects = {
  fetchAppModelsPageTE: (
    userId: string,
    appId: string,
    params?: {
      modelTypeId: string;
      page?: string | number;
      perPage?: string | number;
    },
  ): TaskEither<ReactLeft, CF.API.Models.ListModelsResponse> =>
    pipe(
      fetchAppModelsPageTE({ userId, appId, params }, () => ({ type: UI_STATES.empty })),
      taskEither.map((response) => {
        return response;
      }),
    ),
  fetchWorkflowTE: (
    userId: string,
    appId: string,
    workflowId: string,
    params?: string,
    sessionToken?: string,
    workflowVersionId?: string,
  ): TaskEither<ReactLeft, CF.API.Workflows.Workflow | undefined> =>
    pipe(
      fetchWorkflowTE(
        {
          appId,
          userId,
          workflowId,
          params,
          sessionToken,
          workflowVersionId,
        },
        errorToReactLeft,
      ),
      taskEither.map((x) => {
        if (workflowVersionTypeGuard(x)) {
          const { workflow_id, id, ...versionDetails } = x.workflow_version ?? {};
          return {
            ...versionDetails,
            id: workflow_id,
            version: {
              id,
            },
          };
        }

        return x?.workflow;
      }),
    ),
  listWorkflowsByAppTE: (
    userOrOrgId: string,
    appId: string,
    params?: string,
    sessionToken?: string,
  ): TaskEither<ReactLeft, CF.API.Workflows.Workflow[]> =>
    pipe(
      listWorkflowsByAppTE(
        {
          userOrOrgId,
          appId,
          params,
          sessionToken,
        },
        () => ({ type: UI_STATES.empty }),
      ),
      taskEither.map(({ workflows }) => workflows),
    ),

  listWorkflowsTE: (params?: string): TaskEither<ReactLeft, CF.API.Workflows.Workflow[]> =>
    pipe(
      listWorkflowsTE({ params }, () => ({ type: UI_STATES.empty })),
      taskEither.map(({ workflows }) => workflows),
    ),

  listWorkflowVersionsTE: (
    userId: string,
    appId: string,
    workflowId: string,
    params?: string,
  ): TaskEither<ReactLeft, CF.API.Workflows.WorkflowVersion[]> =>
    pipe(
      listWorkflowVersionsTE(
        {
          userId,
          appId,
          workflowId,
          params,
        },
        () => ({ type: UI_STATES.empty }),
      ),
      taskEither.map(({ workflow_versions }) => workflow_versions),
    ),

  deleteWorkflowTE: (userId: string, appId: string, workflowId: string): TaskEither<ReactLeft, CF.API.Status> =>
    pipe(deleteWorkflowTE({ userId, appId, workflowId }, () => ({ type: UI_STATES.empty }))),
};
